import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import Preloader from './Preloader'; 
import { useJsApiLoader } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = 'AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA';
const GOOGLE_MAPS_LIBRARIES = ['places', 'marker'];

function SOSBeaconPage() {
  const [beacons, setBeacons] = useState([]);
  const [responses, setResponses] = useState({});
  const [profiles, setProfiles] = useState({}); // { user_id: { first_name, last_name } }
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocodeCacheRef = useRef({});
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'deactivated', label: 'Deactivated' },
  ];
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: 'weekly'
  });

  const formatFallbackLocation = (lat, lng) => {
    if (lat === null || lat === undefined || lng === null || lng === undefined || lat === '' || lng === '') {
      return '-';
    }
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return '-';
    return `${latNum.toFixed(5)}, ${lngNum.toFixed(5)}`;
  };

  useEffect(() => {
      document.title = "SOS Beacons";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser && (currentUser.role !== 'admin' && currentUser.role !== 'super admin')) {
      // Optionally, you can redirect: navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    fetchBeacons();
    const beaconChannel = supabase
      .channel('public:sos_beacons')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sos_beacons' },
        (payload) => {
          fetchBeacons();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(beaconChannel);
    };
  }, [statusFilter]);

  async function fetchBeacons() {
    setLoading(true);
    try {
      let query = supabase
        .from('sos_beacons')
        .select('*');
      if (statusFilter && statusFilter !== 'all') query = query.eq('status', statusFilter);
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) {
        setBeacons([]);
        setProfiles({});
      } else {
        setBeacons(data || []);
        // Fetch responses for all beacon ids
        if (data && data.length > 0) {
          const ids = data.map(b => b.id);
          fetchResponses(ids);
          // Collect all user_ids and responder_ids
          const userIds = Array.from(new Set([
            ...data.map(b => b.user_id),
            ...data.map(b => b.responder_id).filter(Boolean)
          ]));
          fetchProfiles(userIds);
        } else {
          setResponses({});
          setProfiles({});
        }
      }
    } catch (error) {
      setBeacons([]);
      setResponses({});
      setProfiles({});
    } finally {
      setLoading(false);
    }
  }

  async function fetchResponses(beaconIds) {
    if (!beaconIds.length) return setResponses({});
    const { data, error } = await supabase
      .from('sos_responses')
      .select('id,sos_beacon_id,responder_id,status,created_at')
      .in('sos_beacon_id', beaconIds);
    if (error) {
      setResponses({});
    } else {
      const grouped = {};
      data.forEach(resp => {
        if (!grouped[resp.sos_beacon_id]) grouped[resp.sos_beacon_id] = [];
        grouped[resp.sos_beacon_id].push(resp);
      });
      setResponses(grouped);
    }
  }

  async function fetchProfiles(userIds) {
    if (!userIds.length) return setProfiles({});
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id,first_name,last_name')
      .in('user_id', userIds);
    if (error) {
      setProfiles({});
    } else {
      const map = {};
      data.forEach(profile => {
        map[profile.user_id] = profile;
      });
      setProfiles(map);
    }
  }

  function getFullName(userId) {
    const p = profiles[userId];
    if (!p) return '-';
    return `${p.first_name || ''} ${p.last_name || ''}`.trim();
  }

  // In-memory, case-insensitive filtering
  const filteredBeacons = beacons.filter(b => {
    // Search by requester or responder name
    const requesterName = getFullName(b.user_id).toLowerCase();
    const responderName = b.responder_id ? getFullName(b.responder_id).toLowerCase() : '';
    const matchesSearch = !search || requesterName.includes(search.toLowerCase()) || responderName.includes(search.toLowerCase());
    // Status filter
    const matchesStatus = statusFilter === 'all' || (b.status && b.status.toLowerCase() === statusFilter.toLowerCase());
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredBeacons.length / itemsPerPage);
  const paginatedBeacons = filteredBeacons.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  async function geocodeBeacons(beaconsList) {
    if (!beaconsList.length) return;
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) return;

    const geocoder = new window.google.maps.Geocoder();
    const updates = {};

    for (const beacon of beaconsList) {
      const lat = parseFloat(beacon.latitude);
      const lng = parseFloat(beacon.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        updates[beacon.id] = '-';
        continue;
      }

      const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      if (geocodeCacheRef.current[cacheKey]) {
        updates[beacon.id] = geocodeCacheRef.current[cacheKey];
        continue;
      }

      try {
        const address = await new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              resolve(results[0].formatted_address);
            } else {
              reject(status);
            }
          });
        });
        geocodeCacheRef.current[cacheKey] = address;
        updates[beacon.id] = address;
      } catch (status) {
        const fallback = formatFallbackLocation(lat, lng);
        geocodeCacheRef.current[cacheKey] = fallback;
        updates[beacon.id] = fallback;
        if (status !== 'ZERO_RESULTS') {
          console.error('Geocoder failed due to:', status);
        }
      }
    }

    if (Object.keys(updates).length) {
      setGeocodedLocations(prev => ({ ...prev, ...updates }));
    }
  }

  useEffect(() => {
    if (!beacons.length) return;

    if (isMapsLoaded && window.google && window.google.maps && window.google.maps.Geocoder) {
      geocodeBeacons(beacons);
      return;
    }

    setGeocodedLocations(prev => {
      const updates = {};
      beacons.forEach(beacon => {
        if (!prev[beacon.id]) {
          updates[beacon.id] = formatFallbackLocation(beacon.latitude, beacon.longitude);
        }
      });
      if (!Object.keys(updates).length) return prev;
      return { ...prev, ...updates };
    });
  }, [beacons, isMapsLoaded]);

  if (!currentUser) return <Preloader text="Loading..." />;
  if (currentUser.role !== 'admin' && currentUser.role !== 'super admin') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar onNarrowChange={setSidebarNarrow}/>
        <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
          <div className="header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Mobile Hamburger Menu */}
              <MobileHamburger
                isOpen={showMobileMenu}
                onToggle={() => setShowMobileMenu(prev => !prev)}
              />
              <div className="title-text">SOS Beacons</div>
             </div>
             <ProfileMenu />
           </div>

           {/* Mobile Dropdown Menu */}
           <MobileMenu 
             isOpen={showMobileMenu} 
             onClose={() => setShowMobileMenu(false)} 
           />

          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <Preloader text="Loading Beacons..." />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">SOS Beacons</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div className="search-actions-row">
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search by Name Responder or Requester"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="type-filter"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: 12 }}><input type="checkbox" checked={selected.length === beacons.length && beacons.length > 0} onChange={e => setSelected(e.target.checked ? beacons.map(b => b.id) : [])} /></th>
                <th style={{ padding: 12 }}>Requester</th>
                <th style={{ padding: 12 }}>Responder</th>
                <th style={{ padding: 12 }}>Location</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Created At</th>
                <th style={{ padding: 12 }}>Arrived</th>
                <th style={{ padding: 12 }}>Responses</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBeacons.length > 0 ? (
                paginatedBeacons.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => navigate(`/sos-beacons/${b.id}`)}>
                    <td style={{ padding: 12 }}><input type="checkbox" checked={selected.includes(b.id)} onChange={e => setSelected(e.target.checked ? [...selected, b.id] : selected.filter(id => id !== b.id))} /></td>
                    <td style={{ padding: 12 }}>{getFullName(b.user_id)}</td>
                    <td style={{ padding: 12 }}>{b.responder_id ? getFullName(b.responder_id) : '-'}</td>
                    <td style={{ padding: 12 }}>{geocodedLocations[b.id] || formatFallbackLocation(b.latitude, b.longitude)}</td>
                    <td style={{ padding: 12 }}>{b.status}</td>
                    <td style={{ padding: 12 }}>{new Date(b.created_at).toLocaleString()}</td>
                    <td style={{ padding: 12 }}>{b.requestee_arrived === true ? <FaCheck style={{color: '#19c37d'}}/> : b.requestee_arrived === false ? <FaXmark style={{color: '#dc3545'}}/> : '-'}</td>
                    <td style={{ padding: 12 }}>
                      {responses[b.id] && responses[b.id].length > 0 ? (
                        <span><FaCheck style={{color: '#19c37d'}}/> {responses[b.id].length} response(s)</span>
                      ) : (
                        <span><FaXmark style={{color: '#dc3545'}}/> No response</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No SOS beacons found.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setPage(i + 1)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    background: page === i + 1 ? '#08b1c7' : '#fff',
                    color: page === i + 1 ? '#fff' : '#333',
                    fontWeight: page === i + 1 ? 'bold' : 'normal'
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SOSBeaconPage;
